import type { PropsWithChildren } from 'react';
import type { Persistence, Auth as WebAuth, User as WebUser } from 'firebase/auth';
import type { Auth as MobileAuth, User as MobileUser } from 'firebase/auth/react-native';

export type Auth = WebAuth | MobileAuth;
export type User = WebUser | MobileUser;

export type Credentials = {
  email: string;
  password: string;
};

export interface IAuthContext {
  isImpersonating: boolean;
  isLoading: boolean;
  isReady: boolean;
  login: (credentials: Credentials) => Promise<User>;
  loginWithToken: (token: string, tokenType: TokenType) => Promise<User>;
  logout: () => Promise<void>;
  reauthenticateUser: (password: string) => Promise<User>;
  updatePassword: (password: string) => Promise<void>;
  user: User | null;
}

export type TokenType = 'impersonate' | 'link-out' | 'embedded';
export type ErrorType = 'set-persistence' | 'pre-token-authentication' | 'token-authentication';

type TokenProps =
  | {
      token?: never;
      tokenType?: never;
      onTokenError?: never;
    }
  | {
      token: string;
      tokenType: TokenType;
      onTokenError: (error: any, tokenType: TokenType) => void;
    };

export type AuthProviderProps = PropsWithChildren<
  {
    auth: Auth;
    testUser?: User & { isImpersonating?: boolean };
    onInitError?: (type: ErrorType, error: any) => void;
  } & TokenProps
>;

export class AuthError extends Error {
  code: string;

  constructor(message: string, code = '') {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
  }
}

// Special authentication handling option provided for impersonation to allow admin to authenticate and manage an individual user per browser tab.
export type PersistenceHandlerType = 'default' | 'impersonate';

export type PeristenceTypeHandlers = {
  [key in PersistenceHandlerType]?: PeristenceHandler | null;
};

export type PeristenceHandler = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  type: Persistence;
};
